import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import styled, { StyledProps } from 'styled-components'
import { TagItem, TagList } from '../../patterns/TagList/TagList'
import { TagsSelectProps } from './TagsSelect'

const TagListWrapper = styled.div`
  margin-top: 16px;
`

const TagListStyled = styled(TagList)`
  display: flex;
  flex-direction: row;
`

const ShowMoreBtn = styled.button`
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  transition: 0.3s color;
  display: inline-flex;
  align-items: center;
  background-color: transparent;
  border: none;
  color: ${({ theme }: StyledProps<{}>) => theme.color.secondary200};
  cursor: pointer;
  padding: 0;

  &:hover {
    color: ${({ theme }: StyledProps<{}>) => theme.color.primary600};
  }

  &:focus {
    outline: none;
  }

  margin-bottom: 14px;
`

export type SelectedOptionsProps<Option> = Pick<
  TagsSelectProps<Option>,
  'collapseCount' | 'getOptionLabel' | 'getOptionValue' | 'value'
> & {
  onRemove: (id: string) => void
}

export const SelectedOptions = <Option extends any>({
  value = [],
  onRemove,
  collapseCount = 5,
  getOptionLabel,
  getOptionValue,
}: SelectedOptionsProps<Option>) => {
  const { t } = useTranslation('ui')
  const [forceOpened, setForceOpened] = useState(false)
  let collapseBtn
  if (value.length > collapseCount) {
    collapseBtn = (
      <ShowMoreBtn
        onClick={e => {
          e.preventDefault()
          setForceOpened(prev => !prev)
        }}
      >
        {forceOpened ? t('TagsSelect.hideSelected') : t('TagsSelect.showSelected')}{' '}
      </ShowMoreBtn>
    )
  }

  const valueMappedToTags: TagItem[] = useMemo(
    () => value.map(v => ({ title: getOptionLabel(v), id: getOptionValue(v) })),
    [getOptionLabel, getOptionValue, value],
  )

  return (
    <TagListWrapper>
      {(!collapseBtn || forceOpened) && <TagListStyled list={valueMappedToTags} onRemove={({ id }) => onRemove(id)} />}
      {collapseBtn}
    </TagListWrapper>
  )
}
